import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Check,
  Copy,
  Loader2,
  X,
  Phone,
  Mail,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  Smartphone,
  Timer,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getServiceName, getServiceIcon, getServiceIconUrl } from "@/lib/hero_services";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { getCountryFlag, getCountryName, getCountryDisplayName } from "@/lib/countries";
import { CountryFlag } from "@/components/country-flag";
import { formatPhoneNumber } from "@/lib/phone";

import { DeliveredCode } from "@/components/delivered-code";
import { useI18n } from "@/lib/i18n";
import { ApiError } from "@/lib/api";
import {
  useActivations,
  useCancelActivation,
  useClearActivationHistory,
  useDeleteActivation,
  useFinishActivation,
} from "@/lib/queries";
import { copyToClipboard } from "@/lib/utils";
import { cancelSecondsLeft } from "@/lib/cancel-lock";
import { parseServerDate } from "@/lib/server-time";

export const Route = createFileRoute("/_app/activations")({
  component: ActivationsPage,
  head: () => ({
    meta: [
      { title: "My Activations — SmsEmpire" },
      { name: "description", content: "Track your SMS and email activations in real time." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Activation = {
  id: number;
  service_code: string;
  number: string | null;
  code: string | null;
  status: string;
  cost: number;
  activation_type: string;
  country: string | null;
  hero_id: string | null;
  created_at: string;
};

function statusColor(s: string) {
  switch (s) {
    case "pending":
      return "bg-amber-500/15 text-amber-400 border-amber-500/30";
    case "received":
      return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
    case "cancelled":
      return "bg-red-500/15 text-red-400 border-red-500/30";
    case "finished":
      return "bg-blue-500/15 text-blue-400 border-blue-500/30";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function statusIcon(s: string) {
  switch (s) {
    case "pending":
      return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
    case "received":
      return <CheckCircle2 className="h-3.5 w-3.5" />;
    case "cancelled":
      return <XCircle className="h-3.5 w-3.5" />;
    case "finished":
      return <Check className="h-3.5 w-3.5" />;
    default:
      return <Clock className="h-3.5 w-3.5" />;
  }
}

function statusLabel(s: string, t: (k: string) => string) {
  switch (s) {
    case "pending":
      return t("activations.status_pending");
    case "received":
      return t("activations.status_received");
    case "cancelled":
      return t("activations.status_cancelled");
    case "finished":
      return t("activations.status_finished");
    default:
      return s;
  }
}

export function ActivationsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"all" | "sms" | "email" | "account">("all");

  // Shared cache with the dashboard and the email page: one poll, not three.
  const {
    data: activations = [],
    isPending: loading,
    isError,
    isFetching,
    refetch,
  } = useActivations();
  const cancelActivation = useCancelActivation();
  const finishActivation = useFinishActivation();
  const removeActivation = useDeleteActivation();
  const clearActivationHistory = useClearActivationHistory();

  function reportError(error: unknown, fallbackKey: string) {
    const message = error instanceof ApiError ? error.message : t(fallbackKey);
    toast.error(message || t(fallbackKey));
  }

  async function cancel(id: number) {
    try {
      const data = await cancelActivation.mutateAsync(id);
      toast.success(t("activations.cancelled_refund", { amount: format(data?.refunded ?? 0) }));
    } catch (error) {
      reportError(error, "activations.cancel_failed");
    }
  }

  async function finish(id: number) {
    try {
      await finishActivation.mutateAsync(id);
      toast.success(t("activations.marked_done"));
    } catch (error) {
      reportError(error, "activations.finish_failed");
    }
  }

  async function deleteActivation(id: number) {
    try {
      await removeActivation.mutateAsync(id);
      toast.success(t("activations.removed"));
    } catch (error) {
      reportError(error, "activations.delete_failed");
    }
  }

  async function clearHistory() {
    try {
      await clearActivationHistory.mutateAsync();
      toast.success(t("activations.history_cleared"));
    } catch (error) {
      reportError(error, "activations.clear_failed");
    }
  }

  async function copy(text: string) {
    if (!text) return;
    const ok = await copyToClipboard(text);
    if (ok) {
      toast.success(t("sms.copied"));
    } else {
      toast.error(t("sms.copy_failed"));
    }
  }

  const filtered = activations.filter((a) => {
    if (filter === "sms") return a.activation_type === "sms";
    if (filter === "email") return a.activation_type === "email";
    if (filter === "account") return a.activation_type === "account";
    return true;
  });

  const pending = filtered.filter((a) => a.status === "pending");
  const received = filtered.filter((a) => a.status === "received");
  const done = filtered.filter((a) => a.status === "cancelled" || a.status === "finished");

  if (!user) return null;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("activations.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("activations.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 self-start sm:self-auto"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          {t("activations.refresh")}
        </Button>
      </div>

      {/* Previously a failed request just left stale rows on screen with no hint. */}
      {isError && (
        <div
          role="alert"
          className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
        >
          {t("activations.load_failed")}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {(["all", "sms", "email", "account"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
              filter === f
                ? "bg-primary text-primary-foreground shadow-glow"
                : "border border-border bg-card text-muted-foreground hover:text-foreground"
            }`}
          >
            {f === "all"
              ? t("activations.all")
              : f === "sms"
                ? "📱 SMS"
                : f === "email"
                  ? "📧 Email"
                  : "👤 Cuentas"}
            <span className="ml-2 rounded-full bg-white/10 px-1.5 py-0.5 text-xs">
              {f === "all"
                ? filtered.length
                : activations.filter((a) => a.activation_type === f).length}
            </span>
          </button>
        ))}
      </div>

      {loading && activations.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center">
          <Smartphone className="mx-auto mb-4 h-12 w-12 text-muted-foreground/40" />
          <p className="text-lg font-semibold">{t("activations.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("activations.empty_desc")}</p>
          <Button
            className="mt-4 bg-gradient-primary shadow-glow"
            onClick={() => navigate({ to: "/dashboard" })}
          >
            {t("activations.go_dashboard")}
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Pending */}
          {pending.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
                <Loader2 className="h-4 w-4 animate-spin text-amber-400" />
                {t("activations.waiting_code")} ({pending.length})
              </h2>
              <div className="space-y-3">
                {pending.map((a) => (
                  <ActivationCard
                    key={a.id}
                    activation={a}
                    onCancel={cancel}
                    onFinish={finish}
                    onCopy={copy}
                    onDelete={deleteActivation}
                    format={format}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Received */}
          {received.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                {t("activations.received_codes")} ({received.length})
              </h2>
              <div className="space-y-3">
                {received.map((a) => (
                  <ActivationCard
                    key={a.id}
                    activation={a}
                    onCancel={cancel}
                    onFinish={finish}
                    onCopy={copy}
                    onDelete={deleteActivation}
                    format={format}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Done */}
          {done.length > 0 && (
            <section>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-base font-semibold text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {t("activations.history")} ({done.length})
                </h2>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t("activations.clear_history")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("activations.clear_confirm_title")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("activations.clear_confirm_desc")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={clearHistory}
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        {t("activations.clear_all")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <div className="space-y-3">
                {done.map((a) => (
                  <ActivationCard
                    key={a.id}
                    activation={a}
                    onCancel={cancel}
                    onFinish={finish}
                    onCopy={copy}
                    onDelete={deleteActivation}
                    format={format}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Cancel timer hook ────────────────────────────────────────────────────────

function useCancelTimer(createdAt: string, status: string) {
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  useEffect(() => {
    if (status !== "pending" || !createdAt) {
      setSecondsLeft(0);
      return;
    }
    const updateTimer = () => setSecondsLeft(cancelSecondsLeft(createdAt, Date.now()));
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [createdAt, status]);

  return secondsLeft;
}

function ActivationCard({
  activation: a,
  onCancel,
  onFinish,
  onCopy,
  onDelete,
  format,
}: {
  activation: Activation;
  onCancel: (id: number) => void;
  onFinish: (id: number) => void;
  onCopy: (text: string) => void;
  onDelete?: (id: number) => void;
  format: (n: number) => string;
}) {
  const { t } = useI18n();
  const isEmail = a.activation_type === "email";
  const isAccount = a.activation_type === "account";
  const isPending = a.status === "pending";
  const isReceived = a.status === "received";

  const cancelSecondsLeft = useCancelTimer(a.created_at, a.status);
  const canCancel = cancelSecondsLeft === 0;

  const serviceName = isAccount
    ? a.number?.replace("ACC-", "Cuenta ") || t("activations.account_title")
    : getServiceName(a.service_code, a.activation_type);
  const serviceIcon = isAccount ? "👤" : getServiceIcon(a.service_code, a.activation_type);
  const serviceIconUrl = getServiceIconUrl(a.service_code);

  const flag = getCountryFlag(a.country);
  const countryName = getCountryName(a.country);
  // Only actual phone numbers get country-formatted — email activations reuse this
  // same field for the delivered email address.
  const displayNumber =
    a.number && !isEmail ? formatPhoneNumber(a.number, a.country) : (a.number ?? "");

  return (
    <div
      className={`rounded-2xl border bg-card p-4 transition-all ${
        isPending
          ? "border-amber-500/30 shadow-sm"
          : isReceived
            ? "border-emerald-500/30 shadow-sm"
            : "border-border opacity-70"
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {/* Icon */}
          <div
            className={`relative flex h-10 w-10 overflow-hidden shrink-0 items-center justify-center rounded-xl text-lg ${
              isAccount
                ? "bg-purple-500/10 text-purple-400"
                : isEmail
                  ? "bg-blue-500/10 text-blue-400"
                  : "bg-primary/10"
            }`}
          >
            {isEmail || isAccount ? (
              serviceIcon
            ) : (
              <>
                <img
                  src={serviceIconUrl}
                  alt={serviceName}
                  className="h-6 w-6 object-contain z-10 relative"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <span className="absolute inset-0 flex items-center justify-center opacity-50 text-sm z-0">
                  {serviceIcon}
                </span>
              </>
            )}
          </div>

          {/* Info */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">{serviceName}</p>
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusColor(a.status)}`}
              >
                {statusIcon(a.status)}{" "}
                {isAccount && isPending
                  ? t("activations.status_pending_account")
                  : statusLabel(a.status, t)}
              </span>
              {a.country && (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-muted/40 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  <CountryFlag country={a.country} size="sm" />
                  <span>{countryName}</span>
                </span>
              )}
            </div>

            {/* Account Pending Note */}
            {isAccount && isPending && (
              <p className="mt-2 text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/20 p-2.5 rounded-xl flex items-center gap-2 font-medium">
                <Clock className="h-3.5 w-3.5 shrink-0 animate-spin" />
                {t("activations.account_pending_desc")}
              </p>
            )}

            {/* Number / Email */}
            {a.number && !isAccount && (
              <button
                onClick={() => onCopy(displayNumber)}
                className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-muted/40 px-2.5 py-1 font-mono text-sm tabular-nums hover:bg-muted transition-colors max-w-full truncate"
              >
                <span className="truncate">{displayNumber}</span>
                <Copy className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            )}

            {/* Account Text / SMS Code */}
            {a.code &&
              (isAccount ? (
                <div className="mt-2 rounded-xl bg-card border border-emerald-500/30 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" />{" "}
                      {t("activations.account_delivered_title")}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onCopy(a.code!)}
                      className="h-7 text-xs flex items-center gap-1 border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-400"
                    >
                      <Copy className="h-3 w-3" /> {t("common.copy")}
                    </Button>
                  </div>
                  <pre className="text-xs font-mono bg-muted/60 p-2.5 rounded-lg whitespace-pre-wrap break-all text-foreground select-all">
                    {a.code}
                  </pre>
                </div>
              ) : (
                <DeliveredCode code={a.code} onCopy={onCopy} />
              ))}

            {/* Date + cost */}
            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
              <span>{new Date(parseServerDate(a.created_at)).toLocaleString()}</span>
              <span>·</span>
              <span className="font-medium text-foreground">{format(a.cost)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
          {isPending && !isAccount && (
            <div className="flex flex-col sm:items-end items-start gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCancel(a.id)}
                disabled={!canCancel}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all whitespace-nowrap shrink-0"
                title={
                  !canCancel
                    ? `Disponible para cancelar en ${cancelSecondsLeft}s`
                    : t("activations.cancel")
                }
              >
                <X className="h-4 w-4 mr-1 shrink-0" />
                {canCancel ? t("activations.cancel") : t("activations.cancel_locked")}
              </Button>
              {!canCancel && (
                <span className="flex items-center gap-1 text-[11px] text-amber-400 font-mono font-medium whitespace-nowrap">
                  <Timer className="h-3 w-3 animate-spin shrink-0" />
                  {Math.floor(cancelSecondsLeft / 60)}:
                  {(cancelSecondsLeft % 60).toString().padStart(2, "0")} min
                </span>
              )}
            </div>
          )}
          {isReceived && !isAccount && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFinish(a.id)}
              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 whitespace-nowrap shrink-0"
            >
              <Check className="h-4 w-4 mr-1 shrink-0" />
              {t("activations.complete")}
            </Button>
          )}
          {isAccount && (isReceived || a.code) && onDelete ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-red-400 hover:bg-red-500/10 shrink-0 text-xs flex items-center gap-1"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("activations.clear_activation")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("activations.clear_confirm_title")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("activations.clear_confirm_desc_single")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(a.id)}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {t("common.confirm")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            !isPending &&
            !isReceived &&
            onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(a.id)}
                className="text-muted-foreground hover:text-red-400 hover:bg-red-500/10 shrink-0"
                title={t("activations.delete_from_history")}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
